import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Base64 } from 'js-base64';
import { WhitelabelConfigStore } from '../core/config/whitelabel-config.store';
import { DataService } from '../services/index';

@Component({
  selector: 'app-success',
  templateUrl: './success.component.html',
  styleUrls: ['./success.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SuccessComponent implements OnInit, OnDestroy {
  private sub: any;
  private readonly afterPrint = () => this.clearPrintMode();

  ticketDetails: any = {};
  bookingdetails: any = {};
  returndetails: any = null;
  confirmData: any = {};

  constructor(
    private router: Router,
    private dataService: DataService,
    public readonly configStore: WhitelabelConfigStore
  ) {}

  ngOnInit(): void {
    this.sub = this.dataService.successValueChange.subscribe(message => {
      const encoded = message || localStorage.getItem('success-buupass');
      if (!encoded) {
        this.router.navigate(['/']);
        return;
      }

      try {
        if (message) {
          localStorage.setItem('success-buupass', message);
        }
        const booking = JSON.parse(Base64.decode(encoded));
        this.bookingdetails = booking?.bookingdetails || {};
        this.returndetails =
          booking?.returndetails ||
          booking?.condfirmData?.returndetails ||
          null;
        this.confirmData = booking?.condfirmData?.confirm_data || {};
        this.ticketDetails =
          booking?.successData?.data?.data ||
          booking?.successData?.data ||
          {};
      } catch {
        this.router.navigate(['/']);
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    window.removeEventListener('afterprint', this.afterPrint);
    this.clearPrintMode();
  }

  get config() {
    return this.configStore.snapshot.config;
  }

  get bookingReference(): string {
    return String(
      this.ticketDetails?.booking_reference ||
      this.ticketDetails?.booking_id ||
      this.confirmData?.data?.booking_reference ||
      this.confirmData?.data?.booking_id ||
      this.confirmData?.data?.ticket_references?.[0] ||
      'Pending'
    );
  }

  get routeFrom(): string {
    return this.confirmData?.from ||
      this.bookingdetails?.search_from ||
      this.bookingdetails?.result?.from ||
      '';
  }

  get routeTo(): string {
    return this.confirmData?.to ||
      this.bookingdetails?.search_to ||
      this.bookingdetails?.result?.to ||
      '';
  }

  get departureTime(): string {
    return this.formatTime(
      this.confirmData?.departure_time || this.bookingdetails?.result?.departure_time
    );
  }

  get arrivalTime(): string {
    return this.formatTime(
      this.confirmData?.arrival_time || this.bookingdetails?.result?.arrival_time
    );
  }

  get travelDate(): string {
    const raw = String(
      this.confirmData?.depature_date ||
      this.bookingdetails?.result?.departure_date ||
      this.bookingdetails?.search_details?.traveldate ||
      ''
    );
    let date: Date | null = null;
    const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    const localMatch = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (isoMatch) {
      date = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    } else if (localMatch) {
      date = new Date(Number(localMatch[3]), Number(localMatch[2]) - 1, Number(localMatch[1]));
    }
    return date && !Number.isNaN(date.getTime())
      ? new Intl.DateTimeFormat('en-GB', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }).format(date)
      : raw;
  }

  get phone(): string {
    const raw = String(this.confirmData?.phoneno || '').replace(/\D/g, '');
    const local = raw.length > 9 ? `0${raw.slice(-9)}` : raw;
    return local.replace(/^(\d{4})(\d{3})(\d{3})$/, '$1 $2 $3');
  }

  get email(): string {
    return String(this.confirmData?.email || '');
  }

  get seatsLabel(): string {
    const seats = this.bookingdetails?.seatsSelected || [];
    if (seats.length) {
      return seats.map((seat: any) => {
        const type = String(seat?.space_type || '').toLowerCase();
        const suffix = type === 'normal' || type === 'standard' ? 'Sta' :
          type ? type.slice(0, 3).replace(/^./, char => char.toUpperCase()) : '';
        return `${seat?.id ?? seat?.seat_number}${suffix ? ` (${suffix})` : ''}`;
      }).join(', ');
    }
    return (this.confirmData?.passengers || [])
      .map((passenger: any) =>
        passenger?.seat_number || passenger?.seat?.id || passenger?.seat?.seat_number
      )
      .filter(Boolean)
      .join(', ');
  }

  get passengerCount(): number {
    return Number(
      this.confirmData?.passengers?.length ||
      this.bookingdetails?.seatsSelected?.length ||
      0
    );
  }

  get passengers(): Array<{
    name: string;
    phone: string;
    seat: string;
    seatClass: string;
  }> {
    const submittedPassengers = Array.isArray(this.confirmData?.passengers)
      ? this.confirmData.passengers
      : [];
    if (submittedPassengers.length) {
      return submittedPassengers.map((passenger: any, index: number) => ({
        name: String(passenger?.full_name || `Passenger ${index + 1}`),
        phone: this.formatPhone(passenger?.phone_number),
        seat: String(
          passenger?.seat?.id ?? passenger?.seat_number ?? passenger?.seat?.seat_number ?? '—'
        ),
        seatClass: this.seatClassLabel(passenger?.seat?.class)
      }));
    }

    return (this.bookingdetails?.seatsSelected || []).map((seat: any, index: number) => ({
      name: `Passenger ${index + 1}`,
      phone: '',
      seat: String(seat?.id ?? seat?.seat_number ?? '—'),
      seatClass: this.seatClassLabel(seat?.space_type)
    }));
  }

  get pickupLabel(): string {
    return String(
      this.confirmData?.boarding_point ||
      this.bookingdetails?.pickup_location ||
      this.bookingdetails?.pickup ||
      ''
    );
  }

  get dropoffLabel(): string {
    return String(
      this.confirmData?.drop_off_point ||
      this.bookingdetails?.dropoff_location ||
      this.bookingdetails?.dropoff ||
      ''
    );
  }

  get operatorLabel(): string {
    const operator = this.confirmData?.operator || this.bookingdetails?.result?.operator;
    const operatorName = typeof operator === 'object'
      ? operator?.name || operator?.operator_name
      : operator;
    const busType =
      this.bookingdetails?.result?.bus_type_name ||
      this.bookingdetails?.result?.bus_type ||
      this.bookingdetails?.result?.make ||
      '';
    return [operatorName || this.config?.site?.name, busType].filter(Boolean).join(' · ');
  }

  get amountPaid(): number {
    const paidAmount = Number(this.confirmData?.payment_amount);
    return Number.isFinite(paidAmount) && paidAmount > 0
      ? paidAmount
      : Number(this.bookingdetails?.totalfareKSH || 0);
  }

  get currencyLabel(): string {
    return this.confirmData?.payment_currency === 'UGX' ? 'UGX' : 'Ksh';
  }

  get paymentMethod(): string {
    const method = String(this.confirmData?.payment_type || 'M-PESA');
    return method.toLowerCase() === 'mpesa' ? 'M-PESA' : method;
  }

  get ticketJourneys(): Array<{
    label: string;
    from: string;
    to: string;
    departureTime: string;
    arrivalTime: string;
    travelDate: string;
    operator: string;
    seats: string;
    passengerCount: number;
    pickup: string;
    dropoff: string;
  }> {
    return [
      this.buildTicketJourney('Outbound', this.bookingdetails, true),
      ...(this.returndetails
        ? [this.buildTicketJourney('Return', this.returndetails, false)]
        : [])
    ];
  }

  downloadTicket(): void {
    document.body.classList.add('ticket-print-active');
    window.addEventListener('afterprint', this.afterPrint, { once: true });
    window.print();
  }

  viewBooking(): void {
    this.router.navigate(['/my-booking']);
  }

  returnHome(): void {
    this.router.navigate(['/']);
  }

  private formatTime(value: any): string {
    const match = String(value || '').match(/(\d{1,2}):(\d{2})/);
    return match ? `${match[1].padStart(2, '0')}:${match[2]}` : String(value || '');
  }

  private buildTicketJourney(label: string, details: any, useConfirmation: boolean) {
    const result = details?.result || {};
    const seats = details?.seatsSelected || [];
    const rawDate = useConfirmation
      ? this.confirmData?.depature_date || result?.departure_date || details?.search_details?.traveldate
      : result?.departure_date || details?.search_details?.traveldate;
    const operator = result?.operator;
    const operatorName = typeof operator === 'object'
      ? operator?.name || operator?.operator_name
      : operator;
    const busType = result?.bus_type_name || result?.bus_type || result?.make || '';

    return {
      label,
      from: String(useConfirmation ? this.routeFrom : details?.search_from || result?.from || ''),
      to: String(useConfirmation ? this.routeTo : details?.search_to || result?.to || ''),
      departureTime: this.formatTime(useConfirmation
        ? this.confirmData?.departure_time || result?.departure_time
        : result?.departure_time),
      arrivalTime: this.formatTime(useConfirmation
        ? this.confirmData?.arrival_time || result?.arrival_time
        : result?.arrival_time),
      travelDate: this.formatTravelDate(rawDate),
      operator: [operatorName || this.config?.site?.name, busType].filter(Boolean).join(' · '),
      seats: this.formatSeats(seats),
      passengerCount: seats.length || (useConfirmation ? this.passengerCount : 0),
      pickup: String(details?.pickup_location || details?.pickup || ''),
      dropoff: String(details?.dropoff_location || details?.dropoff || '')
    };
  }

  private formatSeats(seats: any[]): string {
    return seats.map((seat: any) => {
      const type = String(seat?.space_type || '').toLowerCase();
      const suffix = type === 'normal' || type === 'standard' ? 'Sta' :
        type ? type.slice(0, 3).replace(/^./, char => char.toUpperCase()) : '';
      return `${seat?.id ?? seat?.seat_number}${suffix ? ` (${suffix})` : ''}`;
    }).join(', ');
  }

  private formatTravelDate(value: any): string {
    const raw = String(value || '');
    let date: Date | null = null;
    const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    const localMatch = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (isoMatch) {
      date = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    } else if (localMatch) {
      date = new Date(Number(localMatch[3]), Number(localMatch[2]) - 1, Number(localMatch[1]));
    }
    return date && !Number.isNaN(date.getTime())
      ? new Intl.DateTimeFormat('en-GB', {
          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
        }).format(date)
      : raw;
  }

  private formatPhone(value: any): string {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    return `+${digits}`;
  }

  private seatClassLabel(value: any): string {
    const type = String(value || '').trim().toLowerCase();
    if (!type) {
      return '';
    }
    return type === 'normal' ? 'Standard' : type.charAt(0).toUpperCase() + type.slice(1);
  }

  private clearPrintMode(): void {
    document.body.classList.remove('ticket-print-active');
  }
}
